/**
 * EmptyState — app-agnostic empty/no-data view.
 *
 * Displays an informative empty state when content is not available, with a
 * consistent icon → title → description → optional-action layout. Lifted from
 * the workspace SPA into components/shared so every SPA (admin / tenant /
 * workspace / plugins) can consume one canonical component.
 *
 * Variant presets supply a sensible icon + title + description out of the box;
 * any field can be overridden via props for a fully custom empty state. The
 * component makes NO workspace-specific assumptions.
 *
 * Variants:
 * - noResults: Search / filter returned nothing.
 * - noAccess:  Caller lacks permission for the resource.
 * - notFound:  Resource not found (404).
 * - error:     A fetch FAILED. Not an empty state at all - see the role note.
 * - generic:   Custom / fallback empty state (default).
 *
 * Accessibility (SPEC-FRONTEND-RULE-007 / RULE-010 / RULE-046):
 * - The container is a live region labelled by its title, so screen readers
 *   announce it when it appears. `error` is ASSERTIVE (`role="alert"`);
 *   every other variant is POLITE (`role="status"`). The split is the point
 *   of the variant: SPEC-FRONTEND-RULE-042 requires a failed fetch to render
 *   as a typed error rather than as "no data", and a failure is the one case
 *   here worth interrupting the user for. An authorization refusal
 *   (`noAccess`) stays polite deliberately - it is a state of the world, not
 *   something that just broke - while remaining a DISTINCT state from empty,
 *   which is what RULE-042 / SCEN-008 actually require.
 * - The decorative icon is `aria-hidden` — it adds no information beyond the
 *   title/description text.
 * - The optional action is a real, keyboard-operable MUI <Button> with a
 *   visible focus ring; when loading it is disabled and `aria-busy`.
 */

import React, { type ReactNode } from 'react';
import { Box, Typography, Button } from '@mui/material';
import LockIcon from '@mui/icons-material/Lock';
import SearchOffIcon from '@mui/icons-material/SearchOff';
import FindInPageIcon from '@mui/icons-material/FindInPage';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import InboxIcon from '@mui/icons-material/Inbox';
import { msg } from '@lingui/core/macro';
import { useLingui } from '@lingui/react';
import { colors } from '../../../theme/colors';

export type EmptyStateVariant =
  | 'noResults'
  | 'noAccess'
  | 'notFound'
  | 'error'
  | 'generic';

export interface EmptyStateProps {
  /** Preset that supplies the default icon/title/description. Defaults to `generic`. */
  variant?: EmptyStateVariant;
  /** Overrides the preset title. */
  title?: string;
  /** Overrides the preset description. */
  description?: string;
  /** Overrides the preset icon (decorative — rendered `aria-hidden`). */
  icon?: ReactNode;
  /** Label for the optional action button. Renders the button only when paired with `onAction`. */
  actionText?: string;
  /** Click handler for the optional action button. */
  onAction?: () => void;
  /** Disables the action and shows a loading label while an action is in flight. */
  actionLoading?: boolean;
  /** Tighter vertical rhythm + smaller icon for dense surfaces. */
  compact?: boolean;
}

type VariantDefaults = { icon: ReactNode; title: string; description: string };

const getVariantDefaults = (
  variant: EmptyStateVariant,
  t: (descriptor: ReturnType<typeof msg>) => string,
): VariantDefaults => {
  switch (variant) {
    case 'noAccess':
      return {
        icon: <LockIcon sx={{ fontSize: 40, color: colors.text.secondary }} />,
        title: t(msg`Access Restricted`),
        description: t(
          msg`You do not have permission to access this resource. Please contact your administrator if you believe this is an error.`,
        ),
      };
    case 'noResults':
      return {
        icon: <SearchOffIcon sx={{ fontSize: 40, color: colors.text.secondary }} />,
        title: t(msg`No results found`),
        description: t(msg`Try adjusting your search or filter criteria.`),
      };
    case 'error':
      return {
        // colors.status.error on background.tertiary measures 3.42:1, above the
        // WCAG 2.1 1.4.11 non-text floor of 3:1 - and the icon is aria-hidden
        // decorative anyway, so it carries no information the title does not.
        icon: <ErrorOutlineIcon sx={{ fontSize: 40, color: colors.status.error }} />,
        title: t(msg`Something went wrong`),
        description: t(msg`This content could not be loaded. Please try again.`),
      };
    case 'notFound':
      return {
        icon: <FindInPageIcon sx={{ fontSize: 40, color: colors.text.secondary }} />,
        title: t(msg`Page not found`),
        description: t(msg`The page you're looking for doesn't exist or has been moved.`),
      };
    default:
      return {
        icon: <InboxIcon sx={{ fontSize: 40, color: colors.text.secondary }} />,
        title: t(msg`Nothing here yet`),
        description: t(msg`Content will appear here when available.`),
      };
  }
};

const EmptyState: React.FC<EmptyStateProps> = ({
  variant = 'generic',
  title,
  description,
  icon,
  actionText,
  onAction,
  actionLoading = false,
  compact = false,
}) => {
  const { _: t } = useLingui();
  const defaults = getVariantDefaults(variant, t);

  const displayTitle = title || defaults.title;
  const displayDescription = description || defaults.description;
  const displayIcon = icon || defaults.icon;
  const showAction = Boolean(actionText && onAction);

  return (
    <Box
      // ASSERTIVE for `error` only. Every other variant keeps the polite
      // role="status" it has always had - asserted per variant in the tests,
      // because this conditional is the one genuinely regression-prone edit
      // in the component (RULE-065 precedent: extending a canonical component
      // by a prop proves the untouched paths byte-identical).
      role={variant === 'error' ? 'alert' : 'status'}
      aria-label={displayTitle}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        py: compact ? 4 : 8,
        px: 3,
        textAlign: 'center',
      }}
    >
      <Box
        aria-hidden
        sx={{
          width: compact ? 60 : 80,
          height: compact ? 60 : 80,
          borderRadius: '50%',
          bgcolor: colors.background.tertiary,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          mb: compact ? 2 : 3,
        }}
      >
        {displayIcon}
      </Box>

      <Typography
        variant={compact ? 'subtitle1' : 'h6'}
        sx={{
          fontWeight: 600,
          color: colors.text.primary,
          mb: 1,
        }}
      >
        {displayTitle}
      </Typography>

      <Typography
        variant="body2"
        sx={{
          color: colors.text.secondary,
          maxWidth: 400,
          lineHeight: 1.6,
          mb: showAction ? 3 : 0,
        }}
      >
        {displayDescription}
      </Typography>

      {showAction && (
        <Button
          variant="outlined"
          onClick={onAction}
          disabled={actionLoading}
          aria-busy={actionLoading}
          sx={{
            textTransform: 'none',
            borderColor: colors.border.default,
            color: colors.text.primary,
            '&:hover': {
              bgcolor: colors.background.hover,
              borderColor: colors.border.hover,
            },
          }}
        >
          {actionLoading ? t(msg`Loading...`) : actionText}
        </Button>
      )}
    </Box>
  );
};

export default EmptyState;
